import React from 'react';
import './FloatingHUD.css';

/*
 * App bar. The 60px bar cannot hold the 120px Varahi logotype, so it carries the
 * endorsed-product fallback from BRAND.md: the "Varahi MHI" wordmark with
 * "by Varahi Technologies" set small in mono beneath it, left-aligned. The
 * 40%-of-name-width ratio is not kept at this size; the hierarchy is.
 *
 * Metrics follow the selection: the asset's bar set by default, the selected
 * part's telemetry group when it has one (see src/data/telemetry.js).
 *
 * Agent mode (CONTRACT 10.5): the "Agent" button appears only when agent features are
 * enabled (dev builds with VITE_AGENT_WS_ENDPOINT). In agent mode the bottom control bar
 * would sit over the Workbench drawer, so only the Agent and Voice buttons stay, in the
 * top bar.
 */
const PANEL_BUTTONS = [
  { key: 'diagnostics', icon: '📊', label: 'Diagnostics', title: 'Diagnostics panel' },
  { key: 'dtc', icon: '🔍', label: 'DTC codes', title: 'DTC analysis' },
  { key: 'alerts', icon: '⚠️', label: 'Alerts', title: 'Alerts panel' },
  { key: 'analysis', icon: '🔬', label: 'Analysis', title: 'Advanced analysis' },
  { key: 'chat', icon: '💬', label: 'Chat', title: 'Chat with the vehicle' },
  { key: 'voicePanel', icon: '🎤', label: 'Voice', title: 'Voice assistant' },
  { key: 'kpi', icon: '📈', label: 'KPI', title: 'KPI dashboard' },
  { key: 'aceModel', icon: '⬡', label: 'Failure matrix', title: 'Failure matrix — offline knowledge search', extra: 'ace-model-btn' },
];
const AGENT_BUTTON = { key: 'agent', icon: '◈', label: 'Agent', title: 'Agent mode' };
const AGENT_MODE_KEYS = new Set(['agent', 'voicePanel']);

const FloatingHUD = ({
  telemetry, onPanelToggle, activePanels, assetLabel = 'Light commercial EV', agentEnabled = false, agentMode = false,
}) => {
  if (!telemetry) return null;
  const buttons = agentEnabled ? [...PANEL_BUTTONS, AGENT_BUTTON] : PANEL_BUTTONS;
  const renderButton = ({ key, icon, label, title, extra }) => (
    <button
      key={key}
      className={`control-btn-compact ${extra || ''} ${activePanels[key] ? 'active' : ''}`}
      onClick={() => onPanelToggle(key)}
      title={title}
      aria-pressed={!!activePanels[key]}
    >
      <span className="btn-icon">{icon}</span>
      <span className="btn-label">{label}</span>
    </button>
  );

  return (
    <>
      {/* Compact top app bar */}
      <div className="hud-top-bar fade-in">
        {/* Left: product wordmark + asset context */}
        <div className="hud-section hud-section-left">
          <a
            className="hud-wordmark"
            href="https://varahitechnologies.com/"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Varahi MHI by Varahi Technologies"
          >
            <span className="hud-wordmark__name">
              <span className="hud-wordmark__varahi">Varahi</span> <span className="hud-wordmark__product">MHI</span>
            </span>
            <span className="hud-wordmark__by">by Varahi Technologies</span>
          </a>
          <span className="hud-rule" aria-hidden="true" />
          <div className="vehicle-info">
            <div className="vehicle-name">{assetLabel}</div>
            <div className="vehicle-status">
              <span className="status-dot"></span>
              <span>Active</span>
            </div>
          </div>
        </div>

        {/* Center: live metrics for the asset or the selected part */}
        <div className="hud-section hud-section-center" aria-live="polite">
          {telemetry.barScope === 'part' && (
            <span className="hud-scope" title="Telemetry for the selected part">{telemetry.barTitle}</span>
          )}
          {telemetry.bar.map((metric, i) => (
            <React.Fragment key={metric.key}>
              {i > 0 && <div className="metric-divider"></div>}
              <div className={`metric-compact${metric.status ? ` is-${metric.status}` : ''}`} title={metric.label}>
                <span className="metric-label">{metric.label}</span>
                <span className="metric-value">
                  {metric.text}
                  {metric.unit && <span className="metric-unit">{metric.unit === '%' ? '%' : ` ${metric.unit}`}</span>}
                </span>
              </div>
            </React.Fragment>
          ))}
        </div>

        {/* Right: alerts and location (agent mode: the Agent and Voice buttons) */}
        <div className="hud-section hud-section-right">
          {agentMode && (
            <div className="hud-agent-controls" role="group" aria-label="Agent mode controls">
              {buttons.filter((b) => AGENT_MODE_KEYS.has(b.key)).map(renderButton)}
            </div>
          )}
          {telemetry.alerts > 0 && (
            <div
              className="alerts-badge-compact"
              onClick={() => onPanelToggle('alerts')}
            >
              <span className="alert-count">{telemetry.alerts}</span>
              <span className="alert-label">Alerts</span>
            </div>
          )}
          {telemetry.location && (
            <div className="location-info">
              <span className="location-icon">📍</span>
              <span>{telemetry.location}</span>
            </div>
          )}
        </div>
      </div>

      {/* Bottom control bar (hidden in agent mode, where the drawer sits) */}
      {!agentMode && (
        <div className="hud-control-bar fade-in">
          {buttons.map(renderButton)}
        </div>
      )}
    </>
  );
};

export default FloatingHUD;
